import React from 'react';
import { Chip } from '@nextui-org/react';
import { formatKeyForDisplay } from '../utils/commonutils';
import { formatContactEmailsInput } from '../claim-intake/buildClaimGenerationPayload';
import { firstScalar } from '../claim-intake/claimIntakeParser';
import { ErrorDisplay, type ErrorDisplayProps } from './ErrorDisplay';
import {
  asClaimIntakeRecord,
  CLAIM_INTAKE_HIDDEN_KEYS,
  extractContractsFromActionOutput,
  formatClaimIntakeDisplayValue,
  getClaimIntakePayloadRow,
  hasClaimIntakeRenderablePayload,
} from './claimIntakeActionOutputFormatters';
import {
  ClaimIntakeCollapsibleBlock,
  ClaimIntakeKeyValueRow,
  ClaimIntakeLineItemsList,
  ClaimIntakeSectionBlock,
  ClaimIntakeThreeCsFieldRow,
} from './claimIntakeActionOutputComponents';
import { ClaimIntakeClaimScoreV2Panel } from './ClaimIntakeClaimScoreV2Panel';
import { getActionOutputV2Payload } from './actionOutputV2Utils';

const SECTION_ORDER = ['vin', 'estimateparser', '3csmapper', 'claim score'];

function buildContentKey(value: unknown, prefix: string): string {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    const identifiers = [
      obj.id,
      obj.request_id,
      obj.claim_id,
      obj.contract_id,
      obj.contractNumber,
      obj.contract_number,
      obj.complaint_description,
      obj.complaint,
      obj.cause,
      obj.correction,
    ];
    for (const candidate of identifiers) {
      if (candidate != null && (typeof candidate === 'string' || typeof candidate === 'number')) {
        const str = String(candidate).trim();
        if (str) return `${prefix}-${str.slice(0, 80)}`;
      }
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return `${prefix}-${value}`;
  }
  try {
    const json = JSON.stringify(value);
    if (json) return `${prefix}-${json.length}-${json.slice(0, 80)}`;
  } catch {
    /* ignore */
  }
  return prefix;
}

export type ClaimIntakeActionSection = {
  key: string;
  title: string;
  data: unknown;
  error: ErrorDisplayProps['errorData'] | null;
};

function isHttpErrorStatus(code: unknown): boolean {
  const num = typeof code === 'string' ? Number.parseInt(code, 10) : code;
  return typeof num === 'number' && Number.isFinite(num) && num >= 400;
}

export function checkSectionError(actionData: unknown): ErrorDisplayProps['errorData'] | null {
  if (!actionData || typeof actionData !== 'object' || Array.isArray(actionData)) return null;
  const obj = actionData as Record<string, unknown>;
  if (!obj.error && !obj.body) return null;
  if (hasClaimIntakeRenderablePayload(obj)) return null;

  const errorData: ErrorDisplayProps['errorData'] = {};
  if (typeof obj.error === 'string') {
    errorData.error = obj.error;
  }
  const body = obj.body;
  if (typeof body === 'string' || (body && typeof body === 'object')) {
    errorData.body = body;
  }
  if (obj.body) {
    if (typeof obj.body === 'string') {
      try {
        const parsed = JSON.parse(obj.body);
        errorData.parsedBody = parsed;
        if (!errorData.error && isHttpErrorStatus(parsed?.status_code)) {
          errorData.error = parsed?.message ?? 'Request failed';
        }
      } catch {
        /* ignore */
      }
    } else if (typeof obj.body === 'object') {
      const parsedBody = obj.body as ErrorDisplayProps['errorData']['parsedBody'];
      errorData.parsedBody = parsedBody;
      if (!errorData.error && isHttpErrorStatus(parsedBody?.status_code)) {
        errorData.error = parsedBody?.message ?? 'Request failed';
      }
    }
  }

  if (!errorData.error && !errorData.parsedBody?.message && !errorData.body) return null;
  return errorData;
}

function shouldHideActionOutputKey(key: string): boolean {
  const lower = key.toLowerCase();
  return (
    lower.includes('_json') ||
    lower.includes('current_claim_object') ||
    lower.includes('recipient_details') ||
    lower.includes('is_authorized') ||
    lower.includes('pre_condition_metric') ||
    lower.includes('adjusted_condition_overall') ||
    lower.includes('claim intake object') ||
    lower.includes('source_ids') ||
    lower.includes('source ids') ||
    lower === 'servicer_info'
  );
}

function sectionSortIndex(title: string): number {
  const lower = title.trim().toLowerCase();
  const idx = SECTION_ORDER.findIndex((token) => lower.includes(token));
  return idx === -1 ? 999 : idx;
}

export function buildClaimIntakeActionSections(
  actionOutput: Record<string, unknown> | null | undefined
): ClaimIntakeActionSection[] {
  if (!actionOutput || typeof actionOutput !== 'object') return [];
  return Object.entries(actionOutput)
    .filter(([key]) => !shouldHideActionOutputKey(key))
    .sort(([a], [b]) => {
      const orderDiff = sectionSortIndex(a) - sectionSortIndex(b);
      if (orderDiff !== 0) return orderDiff;
      return a.localeCompare(b);
    })
    .map(([title, data]) => ({
      key: title,
      title,
      data,
      error: checkSectionError(data),
    }));
}

export function getClaimIntakeSectionIcon(title: string): { icon: string; color: string; bg: string; subtitle: string } {
  const lower = title.trim().toLowerCase();
  if (lower.includes('vin')) {
    return { icon: 'mdi:car-info', color: 'text-blue-600', bg: 'bg-blue-100', subtitle: 'Vehicle & contract lookup' };
  }
  if (lower.includes('estimateparser') || lower.includes('estimate parser')) {
    return { icon: 'mdi:file-document-outline', color: 'text-amber-600', bg: 'bg-amber-100', subtitle: 'Parsed invoice & repair lines' };
  }
  if (lower.includes('3csmapper') || lower.includes('3cs mapper')) {
    return { icon: 'mdi:cog-transfer-outline', color: 'text-purple-600', bg: 'bg-purple-100', subtitle: 'Generated claim payload' };
  }
  if (lower.includes('claim score')) {
    return { icon: 'mdi:chart-box-outline', color: 'text-default-500', bg: 'bg-default-100', subtitle: 'Claim score analysis' };
  }
  return { icon: 'mdi:information-outline', color: 'text-default-500', bg: 'bg-default-100', subtitle: 'Workflow output' };
}

function objectToRows(items: unknown[]): Record<string, unknown>[] {
  return items
    .filter((item) => item != null && typeof item === 'object' && !Array.isArray(item))
    .map((item) => item as Record<string, unknown>);
}

function renderThreeCsBlocks(threeCsRaw: unknown) {
  if (!Array.isArray(threeCsRaw) || threeCsRaw.length === 0) {
    return <div className="text-sm text-default-500">No repair lines available</div>;
  }

  const repairItems = threeCsRaw.flatMap((entry, idx) => {
    const row = asClaimIntakeRecord(entry);
    if (!row) return [];
    const title = `Repair ${idx + 1}`;
    return [
      <ClaimIntakeCollapsibleBlock
        key={buildContentKey(row, 'repair')}
        title={
          <Chip size="sm" variant="flat" className="bg-blue-50 text-blue-700">
            {title}
          </Chip>
        }
      >
        <div className="space-y-2">
          <ClaimIntakeSectionBlock title="3Cs" icon="mdi:clipboard-text-outline">
            <ClaimIntakeThreeCsFieldRow label="Complaint" value={row.complaint} fieldKey="complaint" />
            <ClaimIntakeThreeCsFieldRow label="Cause" value={row.cause} fieldKey="cause" />
            <ClaimIntakeThreeCsFieldRow label="Correction" value={row.correction} fieldKey="correction" />
          </ClaimIntakeSectionBlock>
          <ClaimIntakeLineItemsList
            title="Labor lines"
            rows={objectToRows(Array.isArray(row.labor_lines) ? row.labor_lines : [])}
            variant="labor"
          />
          <ClaimIntakeLineItemsList
            title="Part lines"
            rows={objectToRows(Array.isArray(row.part_lines) ? row.part_lines : [])}
            variant="parts"
          />
        </div>
      </ClaimIntakeCollapsibleBlock>,
    ];
  });

  if (!repairItems.length) {
    return <div className="text-sm text-default-500">No repair lines available</div>;
  }

  return <div className="w-full space-y-1">{repairItems}</div>;
}

function isRenderableScalar(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value !== 'object') return true;
  if (Array.isArray(value)) return false;
  const obj = value as Record<string, unknown>;
  return (
    'description' in obj ||
    'code' in obj ||
    'name' in obj ||
    Object.keys(obj).length <= 3
  );
}

function renderScalarFields(obj: Record<string, unknown>, skipKeys: Set<string>) {
  return Object.entries(obj)
    .filter(([key, value]) => !skipKeys.has(key) && !CLAIM_INTAKE_HIDDEN_KEYS.has(key) && isRenderableScalar(value))
    .map(([key, value]) => (
      <ClaimIntakeKeyValueRow key={key} label={formatKeyForDisplay(key)} value={value} fieldKey={key} />
    ));
}

function formatPersonName(person: Record<string, unknown>): string {
  if (typeof person.name === 'string' && person.name.trim()) return person.name.trim();
  const first = typeof person.firstName === 'string' ? person.firstName.trim() : '';
  const last = typeof person.lastName === 'string' ? person.lastName.trim() : '';
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return '--';
}

function renderContractCard(contract: Record<string, unknown>) {
  const vehicle = asClaimIntakeRecord(contract.vehicle);
  const customer = asClaimIntakeRecord(contract.customer);
  const status = asClaimIntakeRecord(contract.status);
  const coverage = asClaimIntakeRecord(contract.coverage);
  const productType = asClaimIntakeRecord(coverage?.productType);

  const vin = vehicle?.vin ?? contract.vin ?? contract.Vehicle_Identification_Number;
  const contractNumber = contract.contractNumber ?? contract.contract_number;
  const dealerNumber = contract.dealerNumber ?? contract.dealer_number;
  const effectiveDate = contract.effectiveDate ?? contract.effective_date;
  const expirationDate = contract.expirationDate ?? contract.expiration_date;

  return (
    <div className="space-y-2">
      <ClaimIntakeSectionBlock title="Vehicle" icon="mdi:car-side">
        <ClaimIntakeKeyValueRow label="VIN" value={vin} fieldKey="vin" />
        {vehicle ? (
          <>
            <ClaimIntakeKeyValueRow label="Year" value={vehicle.vehicleYear ?? vehicle.year} fieldKey="year" />
            <ClaimIntakeKeyValueRow label="Make" value={vehicle.make} fieldKey="make" />
            <ClaimIntakeKeyValueRow label="Model" value={vehicle.model} fieldKey="model" />
            <ClaimIntakeKeyValueRow label="Type" value={vehicle.vehicleType ?? vehicle.type} fieldKey="type" />
          </>
        ) : null}
      </ClaimIntakeSectionBlock>
      <ClaimIntakeSectionBlock title="Contract" icon="mdi:file-document-outline">
        <ClaimIntakeKeyValueRow label="Contract number" value={contractNumber} fieldKey="contract_number" />
        <ClaimIntakeKeyValueRow label="Dealer number" value={dealerNumber} fieldKey="dealer_number" />
        <ClaimIntakeKeyValueRow label="Status" value={status?.description ?? status?.code ?? contract.status} fieldKey="status" />
        <ClaimIntakeKeyValueRow label="Effective date" value={effectiveDate} fieldKey="effective_date" />
        <ClaimIntakeKeyValueRow label="Expiration date" value={expirationDate} fieldKey="expiration_date" />
        <ClaimIntakeKeyValueRow
          label="Coverage plan"
          value={coverage?.description ?? productType?.description ?? productType?.code}
          fieldKey="coverage"
        />
      </ClaimIntakeSectionBlock>
      {customer ? (
        <ClaimIntakeSectionBlock title="Customer" icon="mdi:account-outline">
          <ClaimIntakeKeyValueRow label="Name" value={formatPersonName(customer)} fieldKey="name" />
          <ClaimIntakeKeyValueRow label="Phone" value={customer.phone ?? customer.phone_no} fieldKey="phone_no" />
          <ClaimIntakeKeyValueRow label="Address" value={customer.address} fieldKey="address" />
        </ClaimIntakeSectionBlock>
      ) : null}
    </div>
  );
}

function renderVinSection(data: unknown) {
  const contracts = extractContractsFromActionOutput(data);
  if (contracts.length > 1) {
    return (
      <div className="w-full space-y-1">
        {contracts.map((contract, idx) => {
          const contractNumber = contract.contractNumber ?? contract.contract_number;
          const vehicle = asClaimIntakeRecord(contract.vehicle);
          const title = `Contract ${idx + 1}`;
          const contractKey =
            typeof contractNumber === 'string' || typeof contractNumber === 'number'
              ? String(contractNumber)
              : `idx-${idx}`;
          return (
            <ClaimIntakeCollapsibleBlock
              key={`contract-${contractKey}`}
              title={
                <Chip size="sm" variant="flat" className="bg-indigo-50 text-indigo-700">
                  {title}
                </Chip>
              }
              subtitle={firstScalar(contractNumber) || firstScalar(vehicle?.vin) || 'Contract details'}
            >
              {renderContractCard(contract)}
            </ClaimIntakeCollapsibleBlock>
          );
        })}
      </div>
    );
  }

  if (contracts.length === 1) {
    return renderContractCard(contracts[0]);
  }

  const root = asClaimIntakeRecord(data) ?? {};
  const row = getClaimIntakePayloadRow(data) ?? root;
  const vehicle = asClaimIntakeRecord(row.vehicle);

  return (
    <div className="space-y-2">
      <ClaimIntakeSectionBlock title="Vehicle" icon="mdi:car-side">
        <ClaimIntakeKeyValueRow label="VIN" value={row.vin ?? root.vin ?? row.Vehicle_Identification_Number} fieldKey="vin" />
        {vehicle ? (
          <>
            <ClaimIntakeKeyValueRow label="Year" value={vehicle.vehicleYear ?? vehicle.year} fieldKey="year" />
            <ClaimIntakeKeyValueRow label="Make" value={vehicle.make} fieldKey="make" />
            <ClaimIntakeKeyValueRow label="Model" value={vehicle.model} fieldKey="model" />
            <ClaimIntakeKeyValueRow label="Type" value={vehicle.vehicleType ?? vehicle.type} fieldKey="type" />
          </>
        ) : null}
      </ClaimIntakeSectionBlock>
      <ClaimIntakeSectionBlock title="Contract" icon="mdi:file-document-outline">
        <ClaimIntakeKeyValueRow label="Contract number" value={row.contract_number ?? row.contractNumber} fieldKey="contract_number" />
        <ClaimIntakeKeyValueRow label="Dealer number" value={row.dealer_number ?? row.dealerNumber} fieldKey="dealer_number" />
        <ClaimIntakeKeyValueRow label="Status" value={row.status} fieldKey="status" />
        <ClaimIntakeKeyValueRow label="Effective date" value={row.effective_date ?? row.effectiveDate} fieldKey="effective_date" />
        <ClaimIntakeKeyValueRow label="Expiration date" value={row.expiration_date ?? row.expirationDate} fieldKey="expiration_date" />
      </ClaimIntakeSectionBlock>
      {renderScalarFields(
        row,
        new Set([
          'vin',
          'vehicle',
          'contract_number',
          'contractNumber',
          'dealer_number',
          'dealerNumber',
          'status',
          'effective_date',
          'effectiveDate',
          'expiration_date',
          'expirationDate',
          'data',
          'contracts',
          'body',
          'error',
        ])
      )}
    </div>
  );
}

function renderEstimateParserSection(data: unknown) {
  const root = asClaimIntakeRecord(data) ?? {};
  const row = getClaimIntakePayloadRow(data) ?? root;
  const parserResults =
    asClaimIntakeRecord(row.parser_results) ??
    asClaimIntakeRecord(root.parser_results) ??
    row;

  const metaSkip = new Set(['3Cs', 'data', 'parser_results', 'contracts', 'body', 'error']);
  return (
    <div className="space-y-2">
      <ClaimIntakeSectionBlock title="Invoice & customer" icon="mdi:receipt-text-outline">
        {renderScalarFields(parserResults, metaSkip)}
      </ClaimIntakeSectionBlock>
      <ClaimIntakeSectionBlock title="Repairs (3Cs)" icon="mdi:wrench-clock">
        {renderThreeCsBlocks(parserResults['3Cs'])}
      </ClaimIntakeSectionBlock>
      {!CLAIM_INTAKE_HIDDEN_KEYS.has('source_id') && (root.source_id || row.source_id) ? (
        <div className="text-xs text-default-500 pt-1">
          Source attached: {formatClaimIntakeDisplayValue(root.file_name ?? row.file_name ?? root.source_id ?? row.source_id)}
        </div>
      ) : null}
    </div>
  );
}

type PartyKind = 'customer' | 'servicer';

function renderPartyBlock(title: string, party: Record<string, unknown> | null, kind: PartyKind) {
  if (!party) return null;
  const icon = kind === 'customer' ? 'mdi:account-outline' : 'mdi:store-outline';
  return (
    <ClaimIntakeSectionBlock title={title} icon={icon}>
      <ClaimIntakeKeyValueRow label={formatKeyForDisplay('name')} value={party.name} fieldKey="name" />
      <ClaimIntakeKeyValueRow label={formatKeyForDisplay('phone_no')} value={party.phone_no ?? party.phone_number} fieldKey="phone_no" />
      <ClaimIntakeKeyValueRow
        label={formatKeyForDisplay('email')}
        value={
          typeof party.email === 'string' || Array.isArray(party.email)
            ? formatContactEmailsInput(party.email)
            : party.email
        }
        fieldKey="email"
      />
      <ClaimIntakeKeyValueRow label={formatKeyForDisplay('dealer_no')} value={party.dealer_no} fieldKey="dealer_no" />
      <ClaimIntakeKeyValueRow label={formatKeyForDisplay('address')} value={party.address} fieldKey="address" />
    </ClaimIntakeSectionBlock>
  );
}

function renderUnifiedClaimPayload(row: Record<string, unknown>) {
  const customer = asClaimIntakeRecord(row.customer);
  const servicer = asClaimIntakeRecord(row.servicer);
  const product = asClaimIntakeRecord(row.product);
  const policies = asClaimIntakeRecord(row.policies);
  const requests = Array.isArray(row.requests) ? row.requests : [];

  return (
    <div className="space-y-2">
      <ClaimIntakeSectionBlock title="Claim details" icon="mdi:clipboard-list-outline">
        <ClaimIntakeKeyValueRow label="Failure date" value={row.failure_date} fieldKey="failure_date" />
        <ClaimIntakeKeyValueRow label="Repair order date" value={row.repair_order_date} fieldKey="repair_order_date" />
        <ClaimIntakeKeyValueRow label="Labor rate" value={row.labor_rate} fieldKey="labor_rate" />
        <ClaimIntakeKeyValueRow label="Claim number" value={row.claim_no ?? row.claim_number} fieldKey="claim_no" />
      </ClaimIntakeSectionBlock>
      {renderPartyBlock('Customer', customer, 'customer')}
      {renderPartyBlock('Servicer', servicer, 'servicer')}
      {product ? (
        <ClaimIntakeSectionBlock title="Product" icon="mdi:package-variant-closed">
          <ClaimIntakeKeyValueRow label="Product name" value={product.product_name} fieldKey="product_name" />
          <ClaimIntakeKeyValueRow label="Model" value={product.product_model} fieldKey="product_model" />
          <ClaimIntakeKeyValueRow label="Serial no" value={product.product_serial_no} fieldKey="product_serial_no" />
          <ClaimIntakeKeyValueRow label="In service date" value={product.product_in_service_date} fieldKey="product_in_service_date" />
        </ClaimIntakeSectionBlock>
      ) : null}
      {policies ? (
        <ClaimIntakeSectionBlock title="Policy" icon="mdi:shield-check-outline">
          {Object.entries(policies).map(([key, value]) => (
            <ClaimIntakeKeyValueRow key={key} label={formatKeyForDisplay(key)} value={value} fieldKey={key} />
          ))}
        </ClaimIntakeSectionBlock>
      ) : null}
      {requests.length ? (
        <ClaimIntakeSectionBlock title="Requests" icon="mdi:clipboard-text-multiple-outline">
          <div className="w-full space-y-1">
            {requests.flatMap((request, idx) => {
              const req = asClaimIntakeRecord(request);
              if (!req) return [];
              const parts = Array.isArray(req.parts) ? objectToRows(req.parts) : [];
              const labor = Array.isArray(req.labor) ? objectToRows(req.labor) : [];
              const title = `Request ${idx + 1}`;
              return [
                <ClaimIntakeCollapsibleBlock
                  key={buildContentKey(req, 'request')}
                  title={
                    <Chip size="sm" variant="flat" className="bg-purple-50 text-purple-700">
                      {title}
                    </Chip>
                  }
                >
                  <div className="space-y-2">
                    <ClaimIntakeSectionBlock title="3Cs" icon="mdi:clipboard-text-outline">
                      <ClaimIntakeThreeCsFieldRow label="Complaint" value={req.complaint_description} fieldKey="complaint_description" />
                      <ClaimIntakeThreeCsFieldRow label="Cause" value={req.cause_description} fieldKey="cause_description" />
                      <ClaimIntakeThreeCsFieldRow label="Correction" value={req.corrective_action_description} fieldKey="corrective_action_description" />
                    </ClaimIntakeSectionBlock>
                    <ClaimIntakeLineItemsList title="Labor" rows={labor} variant="labor" />
                    <ClaimIntakeLineItemsList title="Parts" rows={parts} variant="parts" />
                  </div>
                </ClaimIntakeCollapsibleBlock>,
              ];
            })}
          </div>
        </ClaimIntakeSectionBlock>
      ) : null}
    </div>
  );
}

function renderGenericStructuredData(data: unknown, depth: number): React.ReactNode {
  if (depth > 4) {
    return <div className="text-sm text-default-500">{formatClaimIntakeDisplayValue(data)}</div>;
  }
  if (data == null || data === '') {
    return <div className="text-sm text-default-500">--</div>;
  }
  if (typeof data !== 'object') {
    return <ClaimIntakeKeyValueRow label="Value" value={data} />;
  }
  if (Array.isArray(data)) {
    if (data.length === 0) return <div className="text-sm text-default-500">--</div>;
    if (data.every((item) => item != null && typeof item === 'object' && !Array.isArray(item))) {
      return <ClaimIntakeLineItemsList title="Items" rows={objectToRows(data)} variant="default" />;
    }
    return (
      <div className="space-y-2">
        {data.map((item, idx) => (
          <ClaimIntakeSectionBlock key={buildContentKey(item, 'arr')} title={`Item ${idx + 1}`}>
            {renderGenericStructuredData(item, depth + 1)}
          </ClaimIntakeSectionBlock>
        ))}
      </div>
    );
  }

  const obj = data as Record<string, unknown>;
  const entries = Object.entries(obj).filter(([key]) => !CLAIM_INTAKE_HIDDEN_KEYS.has(key));
  if (entries.length === 0) return <div className="text-sm text-default-500">--</div>;

  return (
    <div className="space-y-1">
      {entries.map(([key, value]) => {
        if (value != null && typeof value === 'object') {
          return (
            <ClaimIntakeSectionBlock key={key} title={formatKeyForDisplay(key)}>
              {renderGenericStructuredData(value, depth + 1)}
            </ClaimIntakeSectionBlock>
          );
        }
        return <ClaimIntakeKeyValueRow key={key} label={formatKeyForDisplay(key)} value={value} fieldKey={key} />;
      })}
    </div>
  );
}

export function renderClaimIntakeSectionContent(title: string, data: unknown): React.ReactNode {
  const error = checkSectionError(data);
  if (error) return <ErrorDisplay errorData={error} />;

  const lower = title.trim().toLowerCase();
  if (lower.includes('vin')) return renderVinSection(data);
  if (lower.includes('estimateparser') || lower.includes('estimate parser')) return renderEstimateParserSection(data);
  if (lower.includes('3csmapper') || lower.includes('3cs mapper')) {
    const row = getClaimIntakePayloadRow(data);
    if (row) return renderUnifiedClaimPayload(row);
    return renderGenericStructuredData(data, 0);
  }
  if (lower.includes('claim score')) {
    const v2 = getActionOutputV2Payload(data);
    if (v2) {
      return <ClaimIntakeClaimScoreV2Panel payload={v2} />;
    }
    return renderGenericStructuredData(data, 0);
  }
  return renderGenericStructuredData(data, 0);
}
